import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { useHubToast } from "@/components/ui/HubToast";
import { VoiceToTextMicButton } from "@/components/ui/VoiceToTextMicButton";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";

import { createInventoryDiscrepancyRequest } from "./inventoryDiscrepancyApi";

export function KittingInventoryDiscrepancyModal({
  open,
  initialSku,
  transferOrderId = null,
  transferOrderItemId = null,
  onClose,
}: {
  open: boolean;
  initialSku: string;
  transferOrderId?: number | null;
  transferOrderItemId?: number | null;
  onClose: () => void;
}): JSX.Element | null {
  const canWrite = useHubModuleWrite("manufacturing");
  const { showToast } = useHubToast();
  const [skuId, setSkuId] = useState(initialSku);
  const [sign, setSign] = useState<"minus" | "plus">("minus");
  const [quantity, setQuantity] = useState("1");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) {
      setSkuId(initialSku);
      setSign(transferOrderId != null ? "plus" : "minus");
      setQuantity("1");
      setReason("");
    }
  }, [open, initialSku, transferOrderId]);

  const submitMutation = useMutation({
    mutationFn: () => {
      const magnitude = Number.parseInt(quantity, 10);
      if (!Number.isFinite(magnitude) || magnitude <= 0) {
        throw new Error("Quantity must be a positive integer.");
      }
      const signed = sign === "minus" ? -magnitude : magnitude;
      return createInventoryDiscrepancyRequest({
        sku_id: skuId.trim(),
        quantity: signed,
        reason: reason.trim() === "" ? null : reason.trim(),
        transfer_order_id: transferOrderId,
        transfer_order_item_id: transferOrderItemId,
      });
    },
    onSuccess: () => {
      showToast({
        tone: "success",
        message: "Inventory discrepancy submitted for manufacturing management review.",
      });
      onClose();
    },
    onError: (error: Error) => showToast({ tone: "error", message: error.message }),
  });

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[55] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kitting-discrepancy-title"
    >
      <div className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl">
        <h2 id="kitting-discrepancy-title" className="text-lg font-semibold text-slate-900">
          Note inventory discrepancy (SGS)
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          Report shrinkage (minus) or found extra (plus). Manufacturing management will investigate
          and post Megaventory adjustment or wastage.
        </p>
        <div className="mt-4 space-y-3">
          <div>
            <Label htmlFor="kitting-discrepancy-sku">SKU</Label>
            <Input
              id="kitting-discrepancy-sku"
              value={skuId}
              onChange={(event) => setSkuId(event.target.value)}
              className="font-mono"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="kitting-discrepancy-sign">Direction</Label>
              <select
                id="kitting-discrepancy-sign"
                className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-2 text-sm"
                value={sign}
                onChange={(event) => setSign(event.target.value === "plus" ? "plus" : "minus")}
              >
                <option value="minus">Shrinkage (minus)</option>
                <option value="plus">Found extra (plus)</option>
              </select>
            </div>
            <div>
              <Label htmlFor="kitting-discrepancy-qty">Quantity</Label>
              <Input
                id="kitting-discrepancy-qty"
                type="number"
                min={1}
                value={quantity}
                onChange={(event) => setQuantity(event.target.value)}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="kitting-discrepancy-reason">Reason</Label>
            <div className="mt-1 flex items-stretch gap-2">
              <Input
                id="kitting-discrepancy-reason"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="What you observed on the floor…"
              />
              <VoiceToTextMicButton
                className="h-10 w-10"
                value={reason}
                ariaLabel="Dictate discrepancy reason"
                onValueChange={setReason}
              />
            </div>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={!canWrite || skuId.trim() === "" || submitMutation.isPending}
            isLoading={submitMutation.isPending}
            onClick={() => submitMutation.mutate()}
          >
            Submit
          </Button>
        </div>
      </div>
    </div>
  );
}
